"use client";

import { useState } from "react";
import { SURVEY_TYPE_OPTIONS } from "@/lib/survey-types";

type Props = {
  /** Public Turnstile site key; omit or empty = widget skipped (honeypot + rate limit only). */
  turnstileSiteKey?: string | null;
};

export function ContactForm({ turnstileSiteKey }: Props) {
  const [status, setStatus] = useState<"idle" | "pending" | "ok" | "err">("idle");
  const [message, setMessage] = useState("");
  const turnstileEnabled = Boolean(turnstileSiteKey?.trim());

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("pending");
    setMessage("");
    const form = e.currentTarget;
    const data = new FormData(form);
    const turnstileToken = turnstileEnabled
      ? String(data.get("cf-turnstile-response") ?? "")
      : undefined;
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          phone: data.get("phone"),
          siteAddress: data.get("siteAddress"),
          surveyType: data.get("surveyType"),
          buildingType: data.get("buildingType"),
          message: data.get("message"),
          fax: data.get("fax"),
          turnstileToken,
        }),
      });
      if (res.ok) {
        setStatus("ok");
        setMessage("Thanks — we will be in touch.");
        form.reset();
      } else {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setStatus("err");
        setMessage(body.error ?? "Could not send. Try again later.");
      }
    } catch {
      setStatus("err");
      setMessage("Network error. Check your connection and try again.");
    }
  }

  return (
    <form className="stack contact-form" onSubmit={onSubmit}>
      <div className="hp-field" aria-hidden="true">
        <label>
          Fax
          <input name="fax" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <div className="field">
        <label htmlFor="name">Name</label>
        <input className="input" id="name" name="name" required autoComplete="name" />
      </div>
      <div className="field">
        <label htmlFor="email">Email</label>
        <input
          className="input"
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
        />
      </div>
      <div className="field">
        <label htmlFor="phone">Phone</label>
        <input className="input" id="phone" name="phone" type="tel" autoComplete="tel" />
      </div>
      <div className="field">
        <label htmlFor="surveyType">Survey needed</label>
        <select className="input" id="surveyType" name="surveyType" defaultValue="">
          <option value="">Choose if known</option>
          {SURVEY_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="buildingType">Building type</label>
        <input
          className="input"
          id="buildingType"
          name="buildingType"
          placeholder="e.g. office, school, warehouse"
          autoComplete="off"
        />
      </div>
      <div className="field">
        <label htmlFor="siteAddress">Site address</label>
        <input
          className="input"
          id="siteAddress"
          name="siteAddress"
          autoComplete="street-address"
        />
      </div>
      <div className="field">
        <label htmlFor="message">Message</label>
        <textarea className="input" id="message" name="message" rows={5} required />
      </div>
      {turnstileEnabled ? (
        <div
          className="cf-turnstile"
          data-sitekey={turnstileSiteKey!}
          data-theme="light"
        />
      ) : null}
      <button className="btn btn-primary" type="submit" disabled={status === "pending"}>
        {status === "pending" ? "Sending…" : "Send message"}
      </button>
      {status === "ok" && (
        <p className="muted" role="status">
          {message}
        </p>
      )}
      {status === "err" && (
        <p className="error" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}
